import React, { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Send, ShieldCheck, CheckCircle2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

/**
 * /client-login — request a magic-link.
 * Email-only for launch. Backend sends the one-tap portal link to the
 * email on the client's record. Rate-limited server-side (5/min per IP).
 */
export default function PortalRequestLink() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/public/my-portal/request-link`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Please try again in a moment");
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="mx-auto max-w-md px-6 py-16">
        <div className="mb-6 flex items-center gap-3 text-sm text-primary">
          <ShieldCheck className="h-4 w-4" /> Your personal portal — no password required.
        </div>

        {sent ? (
          <Card className="border-primary/30 bg-primary/5 p-8 text-center" data-testid="link-sent-card">
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-primary" />
            <h1 className="mb-2 text-2xl font-dashboard font-bold tracking-tight">Check your inbox.</h1>
            <p className="text-sm text-muted-foreground">
              If <b>{email}</b> is on our records, we've just emailed you a one-tap link. It's valid for 30 minutes and expires the moment you use it.
            </p>
            <button onClick={() => { setSent(false); setEmail(""); }} className="mt-6 text-sm text-primary hover:underline" data-testid="use-different-email">
              use a different email
            </button>
          </Card>
        ) : (
          <Card className="border-border bg-white p-8">
            <h1 className="mb-2 text-2xl font-dashboard font-bold tracking-tight">Open your portal</h1>
            <p className="mb-6 text-sm text-muted-foreground">
              Enter the email you registered with us. We'll send you a one-tap access link.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="portal-email">Email</Label>
                <Input
                  id="portal-email"
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  data-testid="portal-identifier-input"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full gap-2" data-testid="portal-request-btn">
                <Send className="h-4 w-4" /> {busy ? "Sending link…" : "Send me a link"}
              </Button>
            </form>
            <p className="mt-6 text-center text-xs text-muted-foreground">
              Reliserve Wealth staff?{" "}
              <a href="/" className="text-primary hover:underline" data-testid="staff-login-link">Staff sign in →</a>
            </p>
          </Card>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="marketing hero-bg min-h-screen">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-6 py-4">
          <BrandLogo size="md" />
        </div>
      </header>
      {children}
    </div>
  );
}
